import type { RetentionCurvePoint } from '../../shared/retentionCurvePoint';
import { formatVideoClock } from '../formatVideoClock';
import { chartGeometry } from './chartGeometry';

// Hand-rolled SVG, no charting library (spec §2). This is a polyline; a chart
// library would be a dependency, a bundle, and an abstraction for something that
// is four lines of arithmetic.
//
// Shows how many reviewers were still watching, against video time — the "where
// did they leave" half of the picture. The reaction timeline below it, sharing
// this exact x-axis, is the "why" half.
export function RetentionCurveChart(input: {
  points: RetentionCurvePoint[];
  durationMs: number;
  reviewerCount: number;
  width: number;
  height: number;
}) {
  const geometry = chartGeometry({
    width: input.width,
    height: input.height,
    durationMs: input.durationMs
  });

  // Acceptance criterion 7: zero reviewers must render, not crash. There is
  // simply no curve to draw yet, and saying so is more use than an empty frame.
  if (input.points.length === 0 || input.reviewerCount === 0) {
    return (
      <div className="chartEmpty" style={{ height: input.height }}>
        No watch data yet.
      </div>
    );
  }

  const linePoints = input.points
    .map((point) => `${geometry.xForTime(point.t_ms).toFixed(1)},${geometry.yForFraction(point.pct / 100).toFixed(1)}`)
    .join(' ');

  const areaPath =
    `M ${geometry.xForTime(0).toFixed(1)},${geometry.baselineY.toFixed(1)} ` +
    `L ${linePoints.split(' ').join(' L ')} ` +
    `L ${geometry.xForTime(input.durationMs).toFixed(1)},${geometry.baselineY.toFixed(1)} Z`;

  return (
    <svg
      className="chartSvg"
      viewBox={`0 0 ${input.width} ${input.height}`}
      role="img"
      aria-label="Percentage of reviewers still watching, over video time"
    >
      {[0, 0.5, 1].map((fraction) => (
        <g key={fraction}>
          <line
            x1={geometry.padLeft}
            x2={input.width - geometry.padRight}
            y1={geometry.yForFraction(fraction)}
            y2={geometry.yForFraction(fraction)}
            className="chartGridline"
          />
          <text x={4} y={geometry.yForFraction(fraction) + 4} className="chartAxisLabel">
            {Math.round(fraction * 100)}%
          </text>
        </g>
      ))}

      <path d={areaPath} className="retentionArea" />
      <polyline points={linePoints} className="retentionLine" />

      <text x={geometry.padLeft} y={input.height - 6} className="chartAxisLabel">
        0:00
      </text>
      <text x={input.width - geometry.padRight} y={input.height - 6} className="chartAxisLabel" textAnchor="end">
        {formatVideoClock(input.durationMs)}
      </text>
    </svg>
  );
}
